import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import FlagIcon from "@mui/icons-material/Flag";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import PrimaryButton from "../../../../../Components/Atoms/Buttons/Primary/PrimaryButton";
import DownloadSection from "../DownloadSection/DownloadSection";
import TimerSection from "../TimerSection/TimerSection";
import ReviewModal from "./ReviewModal";
import config from "../../../../../config";
import { BaseURL } from "../../../../../Hooks/URL";
import {
  loadAnswersFromStorage,
  saveAnswerToStorage,
  removeAnswerFromStorage,
  saveFlaggedQuestions,
  loadFlaggedQuestions,
  getUserId,
} from "../../Utils/mcqStorage";
import "./McqInterface.css";

function McqInterface({
  mission,
  timeLeft,
  mcqAnswers,
  mcqSubmitting,
  mcqSubmitStatus,
  mcqReviewData,
  handleMcqAnswerChange,
  handleMcqReview,
  handleMcqSubmit,
  contextData,
  missionId,
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [currentReviewData, setCurrentReviewData] = useState(null);

  // Get user ID for localStorage
  const userId = useMemo(() => getUserId(contextData), [contextData]);

  // Extract questions from mission data
  const questions = useMemo(() => {
    if (
      !mission ||
      mission.task?.type !== "mcq" ||
      !mission.task?.missionTaskMCQ?.questions
    ) {
      // Fallback: create simple questions if structure doesn't exist
      const numberOfQuestions = mission?.task?.numberOfQuestions || 0;
      return Array.from({ length: numberOfQuestions }, (_, index) => ({
        id: index,
        text: `Question ${index + 1}`,
        score: 1,
        answers: [
          { id: 0, symbol: "A", text: "Option A" },
          { id: 1, symbol: "B", text: "Option B" },
          { id: 2, symbol: "C", text: "Option C" },
          { id: 3, symbol: "D", text: "Option D" },
        ],
      }));
    }
    return mission.task.missionTaskMCQ.questions;
  }, [mission]);

  const currentQuestion = questions[currentIndex] || null;
  const isSubmitted = mission?.submission ? true : false;

  // Load answers and flagged questions from localStorage on mount
  useEffect(() => {
    if (userId && missionId && questions.length > 0 && !isSubmitted) {
      // Load flagged questions
      const savedFlagged = loadFlaggedQuestions(userId, missionId);
      if (savedFlagged.size > 0) {
        setFlaggedQuestions(savedFlagged);
      }
    }
  }, [userId, missionId, questions.length, isSubmitted]);

  // Close review modal after successful submission (not review)
  useEffect(() => {
    // Only close modal on actual submission success, not review success
    // Check for "submitted successfully" to distinguish from review success
    if (
      mcqSubmitStatus &&
      (mcqSubmitStatus.includes("submitted successfully") ||
        mcqSubmitStatus.includes("Answers submitted"))
    ) {
      setReviewModalOpen(false);
      setCurrentReviewData(null);
    }
  }, [mcqSubmitStatus]);

  // Calculate total score
  const totalScore = useMemo(() => {
    return questions.reduce(
      (total, question) => total + (parseInt(question.score) || 0),
      0
    );
  }, [questions]);

  // Get selected answer for current question
  const getSelectedAnswer = (questionId, questionIndex) => {
    if (isSubmitted && mission?.submission?.mcqSubmission) {
      const question = mission.submission.mcqSubmission.questions.find((question) => question.id === questionId);
      if (!question) return null;
      const answer = question.answers.find((answer) => answer.id === question.selectedAnswerId);
      if (!answer) return null;
      return answer.symbol;
    }
    // Try multiple key formats for compatibility (string, number, questionId, index)
    const answer =
      mcqAnswers[questionId] ||
      mcqAnswers[String(questionId)] ||
      mcqAnswers[Number(questionId)] ||
      null;

    return answer;
  };

  // Handle answer selection
  const handleAnswerClick = (answerSymbol) => {
    if (isSubmitted) return;

    // Use question ID if available, otherwise use index
    // This works with the updated handleMcqSubmit which checks both
    const questionId = currentQuestion?.id ?? currentIndex;
    handleMcqAnswerChange(questionId, answerSymbol);
  };

  // Handle unselect answer
  const handleUnselectAnswer = () => {
    if (isSubmitted) return;

    const questionId = currentQuestion?.id ?? currentIndex;
    // Clear the answer by passing null or empty string
    handleMcqAnswerChange(questionId, "");
  };

  // Handle review modal confirm (actual submission)
  const handleReviewConfirm = () => {
    setReviewModalOpen(false);
    handleMcqSubmit();
  };

  // Handle initial "Review & Submit" click - send review API then open modal on success
  const handleReviewClick = async () => {
    if (typeof handleMcqReview === "function") {
      try {
        const reviewData = await handleMcqReview();

        // Only open modal if API call succeeded and returned data
        if (reviewData) {
          // Store the review data returned from API
          setCurrentReviewData(reviewData);
          // Open modal after successful API call
          setReviewModalOpen(true);
        }
      } catch (error) {
        console.error("Error in review API:", error);
        // Don't open modal if API fails
        return;
      }
    } else {
      // If handleMcqReview is not provided, just open modal with local data
      setReviewModalOpen(true);
    }
  };

  // Clear review data when modal closes
  const handleReviewModalClose = () => {
    setReviewModalOpen(false);
    setCurrentReviewData(null);
  };

  // Handle flag toggle
  const handleToggleFlag = (questionId, questionIndex) => {
    const id = questionId ?? questionIndex;
    setFlaggedQuestions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }

      // Save to localStorage
      if (userId && missionId) {
        saveFlaggedQuestions(userId, missionId, newSet);
      }

      return newSet;
    });
  };

  // Check if question is flagged
  const isQuestionFlagged = (questionId, questionIndex) => {
    const id = questionId ?? questionIndex;
    return flaggedQuestions.has(id);
  };

  // Check if answer is selected
  const isAnswerSelected = (answerSymbol) => {
    const questionId = currentQuestion?.id ?? currentIndex;
    const selected = getSelectedAnswer(questionId, currentIndex);
    return selected === answerSymbol;
  };

  // Check if question has an answer
  const hasAnswer = (questionId, questionIndex) => {
    const selected = getSelectedAnswer(questionId, questionIndex);
    return selected !== null && selected !== undefined && selected !== "";
  };

  return (
    <Box sx={{ width: "100%" }}>
      {questions.length === 0 ? (
        <Card className="mcqQuestionCard">
          <CardContent>
            <Typography variant="body1" sx={{ textAlign: "center", py: 4 }}>
              No questions found for this exam.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box className="mcqContainer">
          {!isSubmitted && (
            <TimerSection
              timeLeft={timeLeft}
              canSubmit={mission?.missionSubmissionStatus?.canSubmit}
              title="Time Left"
              variant="small"
              direction="row"
            />
          )}
          {/* Submission Success Message */}
          {isSubmitted && (
            <Box className="mcqSubmissionSuccess" sx={{ mb: 2 }}>
              <Typography
                sx={{
                  color: "success.main",
                  fontWeight: 600,
                  fontSize: 16,
                  mb: 2,
                }}
              >
                ✅ Your answers have been submitted successfully!
              </Typography>
              <Typography
                sx={{
                  color: "#666",
                  fontSize: 14,
                }}
              >
                Submitted on:{" "}
                {new Date(mission.submission.submittedAt).toLocaleString()}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              width: "100%",
              display: "flex",
              gap: "20px",
              alignItems: "flex-start",
              flexWrap: { xs: "wrap", md: "nowrap" },
            }}
          >
            {/* Sidebar: Questions Navigation - Only show if not submitted */}
            {!isSubmitted && (
              <Box className="mcqSidebar">
                <Typography variant="h6" className="mcqSidebarTitle">
                  Questions
                </Typography>

                {/* Question List */}
                {questions.map((q, index) => {
                  const questionId = q.id ?? index;
                  const isCurrent = index === currentIndex;
                  const hasAnswerForQuestion = hasAnswer(questionId, index);
                  const isFlagged = isQuestionFlagged(questionId, index);

                  return (
                    <Button
                      key={q.id ?? index}
                      variant={isCurrent ? "contained" : "outlined"}
                      fullWidth
                      onClick={() => setCurrentIndex(index)}
                      className="mcqQuestionButton"
                      sx={{
                        borderColor: isCurrent
                          ? "var(--primary)"
                          : hasAnswerForQuestion
                          ? "#6a0505"
                          : "rgba(0,0,0,0.12)",
                        backgroundColor: isCurrent
                          ? "var(--primary)"
                          : hasAnswerForQuestion
                          ? "#fdf2f2"
                          : "transparent",
                        color: isCurrent
                          ? "var(--text-secondary)"
                          : hasAnswerForQuestion
                          ? "#7a1515"
                          : "var(--text-primary)",
                        justifyContent: "flex-start",
                        "&:hover": {
                          backgroundColor: isCurrent
                            ? "var(--primary)"
                            : hasAnswerForQuestion
                            ? "#f8dcdc"
                            : "transparent",
                          color: isCurrent
                            ? "var(--text-secondary)"
                            : hasAnswerForQuestion
                            ? "#7a1515"
                            : "var(--text-primary)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                          gap: 1,
                        }}
                      >
                        <span
                          style={{
                            marginRight: "4px",
                            display: "flex",
                            alignItems: "center",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Question {index + 1}
                        </span>
                        {/* <span className="mcqQuestionButtonText">
                        {q.text || `Question ${index + 1}`}
                      </span> */}
                        {isFlagged && (
                          <FlagIcon
                            sx={{
                              fontSize: "16px",
                              color: "#f97316",
                              ml: "auto",
                              flexShrink: 0,
                            }}
                          />
                        )}
                      </Box>
                    </Button>
                  );
                })}
              </Box>
            )}

            {/* Main: Question Display */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {isSubmitted ? (
                /* Show all questions when submitted */
                <Box
                  sx={{
                    // maxHeight: "calc(100vh - 200px)",
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    pr: 1,
                  }}
                >
                  {questions.map((question, qIndex) => {
                    const questionId = question.id ?? qIndex;

                    return (
                      <Card key={questionId} className="mcqQuestionCard">
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 2,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              className="mcqQuestionNumber"
                              sx={{ mb: 0 }}
                            >
                              Question {qIndex + 1} of {questions.length}{" "}
                              (Score: {question.score})
                            </Typography>
                          </Box>

                          {/* Question Image */}
                          {question.imagePath && (
                            <img
                              src={`${BaseURL}/${question.imagePath}`}
                              alt="Question"
                              style={{
                                maxHeight: "300px",
                                maxWidth: "100%",
                                marginBottom: "10px",
                                borderRadius: "4px",
                              }}
                            />
                          )}

                          {/* Question Text */}
                          {question.text && (
                            <Typography
                              variant="h5"
                              className="mcqQuestionText"
                            >
                              {question.text}
                            </Typography>
                          )}

                          {/* Answer Options */}
                          {question.answers && (
                            <Box className="mcqAnswersList">
                              {question.answers.map((answer, aIndex) => {
                                const answerSymbolOption =
                                  answer.symbol ||
                                  String.fromCharCode(65 + aIndex);
                                // Check if this answer was selected for this question
                                const questionAnswer = getSelectedAnswer(
                                  questionId,
                                  qIndex
                                );
                                const isSelected =
                                  answerSymbolOption === questionAnswer;
                                const answerId = answer.id ?? aIndex;

                                return (
                                  <div
                                    key={answer.id ?? aIndex}
                                    className={`mcqAnswerRow ${
                                      isSelected ? "mcqAnswerRowSelected" : ""
                                    } mcqAnswerRowDisabled`}
                                  >
                                    <span className="mcqAnswerSymbol">
                                      {answerSymbolOption}
                                    </span>
                                    <Box className="mcqAnswerContent">
                                      {answer.imagePath && (
                                        <img
                                          src={`${BaseURL}/${answer.imagePath}`}
                                          alt="Answer"
                                          className="mcqAnswerImage"
                                        />
                                      )}
                                      {answer.text && (
                                        <span className="mcqAnswerText">
                                          {answer.text}
                                        </span>
                                      )}
                                    </Box>
                                    {isSelected && (
                                      <span className="mcqAnswerBadge">
                                        Selected
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              ) : currentQuestion ? (
                /* Show single question when not submitted */
                <Card className="mcqQuestionCard">
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        className="mcqQuestionNumber"
                        sx={{ mb: 0 }}
                      >
                        Question {currentIndex + 1} of {questions.length}{" "}
                        (Score: {currentQuestion.score})
                        {/* {typeof currentQuestion.score !== "undefined" && (
                          <Typography
                            variant="body2"
                            className="mcqQuestionScore"
                          >
                            
                          </Typography>
                        )} */}
                      </Typography>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        {/* Flag Button */}
                        <IconButton
                          onClick={() =>
                            handleToggleFlag(
                              currentQuestion?.id ?? currentIndex,
                              currentIndex
                            )
                          }
                          disabled={isSubmitted}
                          sx={{
                            color: isQuestionFlagged(
                              currentQuestion?.id ?? currentIndex,
                              currentIndex
                            )
                              ? "#f97316"
                              : "#6b7280",
                            "&:hover": {
                              backgroundColor: "rgba(249, 115, 22, 0.1)",
                            },
                          }}
                        >
                          {isQuestionFlagged(
                            currentQuestion?.id ?? currentIndex,
                            currentIndex
                          ) ? (
                            <FlagIcon />
                          ) : (
                            <FlagOutlinedIcon />
                          )}
                        </IconButton>
                        {/* Unselect Button */}
                        {hasAnswer(
                          currentQuestion?.id ?? currentIndex,
                          currentIndex
                        ) &&
                          !isSubmitted && (
                            <Button
                              size="small"
                              startIcon={<ClearIcon />}
                              onClick={handleUnselectAnswer}
                              sx={{
                                fontSize: "12px",
                                padding: "4px 12px",
                                borderRadius: "5px",
                                textTransform: "none",
                                color: "#dc2626",
                                border: "1px solid #dc2626",
                                backgroundColor: "transparent",
                                "&:hover": {
                                  backgroundColor: "rgba(220, 38, 38, 0.05)",
                                },
                              }}
                            >
                              Clear Answer
                            </Button>
                          )}
                      </Box>
                    </Box>

                    {/* Question Image */}
                    {currentQuestion.imagePath && (
                      <img
                        src={`${BaseURL}/${currentQuestion.imagePath}`}
                        alt="Question"
                        style={{
                          maxHeight: "300px",
                          maxWidth: "100%",
                          marginBottom: "10px",
                          borderRadius: "4px",
                        }}
                      />
                    )}

                    {/* Question Text */}
                    {currentQuestion.text && (
                      <Typography variant="h5" className="mcqQuestionText">
                        {currentQuestion.text}
                      </Typography>
                    )}

                    {/* Answer Options */}
                    {currentQuestion.answers && (
                      <Box className="mcqAnswersList">
                        {currentQuestion.answers.map((answer, aIndex) => {
                          const answerSymbol =
                            answer.symbol || String.fromCharCode(65 + aIndex);
                          const isSelected = isAnswerSelected(answerSymbol);
                          const answerId = answer.id ?? aIndex;

                          return (
                            <div
                              key={answer.id ?? aIndex}
                              className={`mcqAnswerRow ${
                                isSelected ? "mcqAnswerRowSelected" : ""
                              } ${isSubmitted ? "mcqAnswerRowDisabled" : ""}`}
                              onClick={() => handleAnswerClick(answerSymbol)}
                            >
                              <span className="mcqAnswerSymbol">
                                {answerSymbol}
                              </span>
                              <Box className="mcqAnswerContent">
                                {answer.imagePath && (
                                  <img
                                    src={`${BaseURL}/${answer.imagePath}`}
                                    alt="Answer"
                                    className="mcqAnswerImage"
                                  />
                                )}
                                {answer.text && (
                                  <span className="mcqAnswerText">
                                    {answer.text}
                                  </span>
                                )}
                              </Box>
                              {isSelected && isSubmitted && (
                                <span className="mcqAnswerBadge">Selected</span>
                              )}
                            </div>
                          );
                        })}
                      </Box>
                    )}

                    {/* Question Navigation */}
                    <Box className="mcqQuestionNav">
                      <Button
                        startIcon={
                          <ArrowBackIcon
                            sx={{ width: "20px", height: "20px" }}
                          />
                        }
                        disabled={currentIndex === 0 || isSubmitted}
                        onClick={() =>
                          setCurrentIndex((prev) => Math.max(prev - 1, 0))
                        }
                        sx={{
                          fontSize: "14px",
                          padding: "6px 16px",
                          borderRadius: "5px",
                          textTransform: "none",
                          color: "var(--primary)",
                          border: "1px solid var(--primary)",
                          backgroundColor: "transparent",
                          "&:hover": {
                            backgroundColor: "rgba(6, 131, 153, 0.05)",
                          },
                          "&:disabled": {
                            borderColor: "rgba(0,0,0,0.12)",
                            color: "rgba(0,0,0,0.26)",
                          },
                        }}
                      >
                        Previous question
                      </Button>
                      <Button
                        endIcon={
                          <ArrowForwardIcon
                            sx={{
                              marginLeft: "6px",
                              width: "20px",
                              height: "20px",
                            }}
                          />
                        }
                        disabled={
                          currentIndex === questions.length - 1 || isSubmitted
                        }
                        onClick={() =>
                          setCurrentIndex((prev) =>
                            Math.min(prev + 1, questions.length - 1)
                          )
                        }
                        sx={{
                          fontSize: "14px",
                          padding: "6px 16px",
                          borderRadius: "5px",
                          textTransform: "none",
                          color: "var(--primary)",
                          border: "1px solid var(--primary)",
                          backgroundColor: "transparent",
                          "&:hover": {
                            backgroundColor: "rgba(6, 131, 153, 0.05)",
                          },
                          "&:disabled": {
                            borderColor: "rgba(0,0,0,0.12)",
                            color: "rgba(0,0,0,0.26)",
                          },
                        }}
                      >
                        Next question
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ) : null}

              {/* Submit Section */}
              {!isSubmitted && (
                <Box className="mcqSubmitSection">
                  <PrimaryButton
                    classBtn={"center"}
                    btnType={"button"}
                    text={
                      mcqSubmitting
                        ? "Submitting..."
                        : "Review & Submit Answers"
                    }
                    click={handleReviewClick}
                    style={{
                      textTransform: "capitalize",
                      color: config.SECONDARY_COLOR,
                      backgroundColor: config.PRIMARY_COLOR,
                      fontWeight: 700,
                      fontSize: 18,
                      minWidth: "200px",
                      padding: "6px 25px",
                    }}
                    disabled={mcqSubmitting}
                  />
                </Box>
              )}

              {/* Submission Status */}
              {mcqSubmitStatus && (
                <Typography
                  className="mcqSubmissionStatus"
                  sx={{
                    color: mcqSubmitStatus.includes("success")
                      ? "success.main"
                      : "error.main",
                  }}
                >
                  {mcqSubmitStatus}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Submission Success Message */}
          {isSubmitted && (
            <Box className="mcqSubmissionSuccess" sx={{ mt: 2 }}>
              <Typography
                sx={{
                  color: "success.main",
                  fontWeight: 600,
                  fontSize: 16,
                  mb: 2,
                }}
              >
                ✅ Your answers have been submitted successfully!
              </Typography>
              <Typography
                sx={{
                  color: "#666",
                  fontSize: 14,
                }}
              >
                Submitted on:{" "}
                {new Date(mission.submission.submittedAt).toLocaleString()}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Review Modal */}
      <ReviewModal
        open={reviewModalOpen}
        onClose={handleReviewModalClose}
        onConfirm={handleReviewConfirm}
        questions={questions}
        mcqAnswers={mcqAnswers}
        reviewData={currentReviewData}
        isSubmitting={mcqSubmitting}
      />
    </Box>
  );
}

export default McqInterface;
